<template>
  <section class="section-container section-container-wider">
    <div class="section-container-header-box fade-up-animation">
      <p class="list-text">Careers</p>
    </div>
    <h1 class="section-container-heading">
      <p
        v-for="line in headingLines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h1>
  </section>

  <Careers />

  <ClientOnly>
    <ContactForm />
  </ClientOnly>
</template>

<script lang="ts" setup>
import { getActiveJobs, getRotationWindow } from '@/data/jobs';

const headingLines = [
  { text: 'Build the work', special: false },
  { text: 'you want to', special: false },
  { text: 'sign your name to.', special: true },
];

// Structured data for the openings currently in rotation, so each one is
// eligible for Google's job-posting results. Computed from the same date-driven
// source as the visible list — may be empty during a quiet stretch, in which
// case we emit only the page + breadcrumb schema.
const now = new Date();
const activeJobs = getActiveJobs(now);
// Posted when the current wave began, valid until it rolls over — keeps the
// JobPosting markup valid (datePosted is required) and honest about the cadence.
const { datePosted, validThrough } = getRotationWindow(now);

const breadcrumb = {
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://vortec.io' },
    { '@type': 'ListItem', position: 2, name: 'Careers', item: 'https://vortec.io/careers' },
  ],
};

const jobsJsonLd = activeJobs.length
  ? [
      {
        '@type': 'ItemList',
        name: 'Open roles | VORTEC',
        url: 'https://vortec.io/careers',
        itemListElement: activeJobs.map((job, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: job.title,
          url: `https://vortec.io/careers#${job.slug}`,
        })),
      },
      ...activeJobs.map((job) => ({
        '@type': 'JobPosting',
        title: job.title,
        description: job.description,
        datePosted,
        validThrough,
        employmentType: job.type.toUpperCase().replace(/[^A-Z]/g, '_'),
        hiringOrganization: {
          '@type': 'Organization',
          name: 'VORTEC',
          sameAs: 'https://vortec.io',
        },
        jobLocationType: 'TELECOMMUTE',
        applicantLocationRequirements: {
          '@type': 'Country',
          name: 'EU',
        },
        url: `https://vortec.io/careers#${job.slug}`,
      })),
    ]
  : [];

useSeo({
  title: 'Careers | VORTEC',
  description: activeJobs.length
    ? 'Join VORTEC. We hire in small waves for engineering and design — see the roles currently open and apply, or reach out if you are close to the brief.'
    : 'We hire in small waves at VORTEC. Nothing is open right now, but we keep great CVs on file and reach out when the next wave opens up.',
  path: '/careers',
  jsonLd: [...jobsJsonLd, breadcrumb],
});
</script>
